import { useEffect, useState } from "react";
import { BarChart3, TrendingUp, MousePointerClick, UserPlus, BadgeDollarSign, Wallet, Activity } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";

const STAGE_ICONS = [MousePointerClick, UserPlus, Activity, BadgeDollarSign, Wallet];

function fmt(v) {
  const n = Number(v || 0);
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export default function Funnel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/aff-panel/funnel?days=${days}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load funnel");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const stages = data?.stages || [];

  return (
    <div data-testid="funnel-page">
      <PageHeader
        eyebrow="ANALYTICS"
        title="Conversion Funnel"
        subtitle="Track Clicks → Registration → FTD → Deposit → Net Revenue."
        right={(
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="p-input-shell rounded-xl px-3 h-10 text-[13px]"
            data-testid="funnel-window-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        )}
      />

      {loading ? (
        <div className="p-glass p-10 text-center text-white/55">Loading funnel…</div>
      ) : !data ? (
        <div className="p-glass p-10 text-center text-white/55">No data yet — share your promo link to start tracking.</div>
      ) : (
        <>
          {/* Stage cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="funnel-stages">
            {stages.map((s, i) => {
              const Icon = STAGE_ICONS[i] || BarChart3;
              return (
                <div key={s.name} className="p-glass p-4">
                  <div className="flex items-center gap-2">
                    <Icon size={14} className="text-emerald-300" />
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/55">{s.name}</div>
                  </div>
                  <div className="mt-2 text-2xl font-black text-white">{fmt(s.value)}</div>
                  {i > 0 ? (
                    <div className="mt-1 text-[10.5px] text-white/45">
                      <span className="text-emerald-300 font-bold">{s.pct_of_prev}%</span> of previous
                    </div>
                  ) : (
                    <div className="mt-1 text-[10.5px] text-white/45">Top of funnel</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bar chart */}
          <div className="p-glass p-5">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-200 mb-3">
              Funnel breakdown ({data.window_days} days)
            </div>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={stages.map((s, i) => ({ ...s, idx: i }))} margin={{ top: 12, right: 12, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                  <XAxis dataKey="name" stroke="#9ca3af" fontSize={11} />
                  <YAxis stroke="#9ca3af" fontSize={11} />
                  <Tooltip
                    contentStyle={{ background: "#0a070f", border: "1px solid rgba(34,192,115,0.4)", borderRadius: 8 }}
                    labelStyle={{ color: "#d4af37", fontWeight: 700 }}
                    formatter={(v) => fmt(v)}
                  />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {stages.map((_, i) => (
                      <Cell key={i} fill={["#22c073", "#34d399", "#d4af37", "#fbbf24", "#f59e0b"][i % 5]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Totals strip */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-4 text-[11px]">
            {Object.entries(data.totals || {}).map(([k, v]) => (
              <div key={k} className="p-glass-light p-3">
                <div className="uppercase tracking-[0.18em] text-white/45 text-[9.5px] font-bold">{k.replace(/_/g, " ")}</div>
                <div className="text-white font-black text-[14px] mt-0.5">{fmt(v)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
